import { Carousel, GridCol, GridContainer, Icon, ICONS, mixins, useMediaDevice } from '@bob/library'
import Heading from '@websrc/App/Components/Heading/Heading'
import { spotWrapperProps } from '@websrc/App/Helpers/spotWrapperProps'
import { mapIconExplainer } from '@websrc/DataMappers/mapSpot'
import { SpotLinkAnchor } from '@websrc/Spot/Link/SpotLinkAnchor'
import React, { useEffect, useState } from 'react'

import { LearnMoreButtonType } from '../Collapse/SpotCollapse'
import * as SC from './SpotIconExplainer.style'

export interface SpotIconExplainerProps {
  spot: ReturnType<typeof mapIconExplainer>['icon_explainer']
  LearnMoreButton?: LearnMoreButtonType
  renderAsCarousel?: boolean
  withinExpandable?: boolean
  isTopOfPage?: boolean
}

/** Private component that renders the actual content */
const Content = ({
  spot,
  LearnMoreButton,
  renderAsCarousel = true,
  isTopOfPage,
}: SpotIconExplainerProps) => {
  const [itemsPerPage, setItemsPerPage] = useState<number>(4)
  const device = useMediaDevice()

  const itemsToRender = [...spot.items]

  useEffect(() => {
    switch (device) {
      case 'desktop':
        setItemsPerPage(4)
        break
      case 'tablet':
        setItemsPerPage(2)
        break
      default:
        setItemsPerPage(1)
    }
  }, [device])

  const renderItems = () =>
    itemsToRender.map(({ item }, i) => (
      <SC.ExplainerCard key={i}>
        {ICONS[item.icon] && (
          <Icon
            color={mixins.colors.grayBlack}
            icon={ICONS[item.icon]}
            size={44}
            style={{ marginBottom: '20px' }}
          />
        )}
        <Heading
          description={item.text}
          headline={item.title}
          level={isTopOfPage ? 2 : 3}
          size={'s'}
          style={{ gap: '10px', display: 'flex', flexDirection: 'column' }}
        />
        {item.link.cta_text && (
          <SpotLinkAnchor
            ctaType={'tertiary'}
            link={item.link}
            linkSection={[spot.headline]}
            linkSubSection={[item.title]}
            style={{ marginTop: '20px' }}
          >
            <Icon icon={ICONS.ARROW_RIGHT} size={16} />
            <span>{item.link.cta_text}</span>
          </SpotLinkAnchor>
        )}
      </SC.ExplainerCard>
    ))

  return (
    <div>
      <Heading
        headline={spot.headline}
        label={spot.caption}
        level={isTopOfPage ? 1 : 2}
        size={'xxl'}
        skipWidth={true}
        style={{ marginBottom: '40px' }}
      />
      {Boolean(itemsToRender.length) && renderAsCarousel && (
        <Carousel itemsPerPage={itemsPerPage}>{renderItems()}</Carousel>
      )}
      {Boolean(itemsToRender.length) && !renderAsCarousel && (
        <SC.Stacked>{renderItems()}</SC.Stacked>
      )}
      {LearnMoreButton && (
        <SC.LearnMoreButtonWrapper>
          <LearnMoreButton />
        </SC.LearnMoreButtonWrapper>
      )}
    </div>
  )
}

export const SpotIconExplainer = (props: SpotIconExplainerProps) => {
  const { spot, withinExpandable } = props

  if (withinExpandable) {
    return <Content {...props} />
  }

  return (
    <GridContainer>
      <GridCol {...spotWrapperProps(spot.caption)}>
        <Content {...props} />
      </GridCol>
    </GridContainer>
  )
}
